import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalService } from '@usitsdasdesign/dds-ng/modal';
import { CloseOutService } from '../../../../http/intake/close-out.service';
import { Subject, takeUntil } from 'rxjs';
import {
  WbsChargeCodeDetailsModel,
  wbsChargeCodeRequestModel,
  WbsResponseModel,
} from '../../../../common/models/wbs-picker.model';
import { SelectItemOptions } from '@usitsdasdesign/dds-ng/select';
import { SubmissionService } from '../../../../http/intake/submission.service';
import {
  WbsDropdownModelResponseAll,
  WbsDropdownResponseModel,
} from '../../../../common/models/wbs-dropdown.model';
import {
  ArchiveClosedModel,
  ArchiveSaveModel,
} from '../../../../common/models/archive-save.model';
import { ArchiveDetailsModel } from '../../../../common/models/archive-details.model';
import { IntakeConstant } from '../../../constants/intake.constant';
import { ProgressIndicatorService } from '../../../../common/services/progress-indicator.service';

@Component({
  selector: 'app-summary-archive-popup',
  templateUrl: './summary-archive-popup.component.html',
  styleUrls: ['./summary-archive-popup.component.less'],
})
export class SummaryArchivePopupComponent implements OnInit, OnDestroy {
  @Input() isFooter: boolean = true;
  @Input() size: string = 'md';
  @Input() wbsChargeCode: null | string = '';
  @Input() wbsNumber: string = '';
  @Input() dataSubmissionId: number | null = null;
  @Input() dataOpportunityId: string = '';
  @Input() initialSelectedArchive: string = ''; // New input property

  actionDropdownList: SelectItemOptions[] = [];
  wbsDescription: string = '';
  wdsTitle: string = '';
  searchList: string[] = [];
  selectedArchive: string = '';
  archiveNumber: string = '';
  readonly #unsubscriber$: Subject<void> = new Subject<void>();
  readonly #wbsUnsubscriber$: Subject<void> = new Subject<void>();
  wbsAPIData!: WbsChargeCodeDetailsModel[];
  archiveClosed!: ArchiveClosedModel;
  dropdowndata!: WbsDropdownResponseModel[];
  archiveSaveRequestBody!: ArchiveSaveModel;
  selectedArchiveDropdown: WbsDropdownResponseModel | undefined;
  isSaveButtonDisabled: boolean = true;
  archiveStatus: string = '';
  archiveDescription: string = '';
  archiveID: string = '';
  archiveName: string = '';
  archiveFoldersArray: string[] = [];
  archiveFolder: string = '';
  business: string = '';
  clientNumber: string = '';
  clientName: string = '';
  partner: string = '';
  archiveUrl: string = '';
  isFetchingWBS: boolean = false;
  totalCount: number = 0;

  constructor(
    public modalService: ModalService,
    private readonly closeoutService: CloseOutService,
    public intakeService: SubmissionService,
    private readonly progressIndicatorService: ProgressIndicatorService
  ) {}

  ngOnInit(): void {
    this.fetchWbsChargeCode();
    this.selectedArchive = this.initialSelectedArchive;
  }

  // 1 -> scroll
  fetchWbsChargeCode(callFrom: number = 0): void {
    if (callFrom !== 1) {
      this.searchList = [];
    }
    this.isFetchingWBS = true;
    const requestBody = this.createRequestBody();
    this.closeoutService
      .getWbsChargeCodes(requestBody)
      .pipe(takeUntil(this.#wbsUnsubscriber$))
      .subscribe({
        next: (response: WbsResponseModel) => {
          if (response) {
            this.totalCount = response.totalCount;

            if (callFrom === 1) {
              this.wbsAPIData = [
                ...this.wbsAPIData,
                ...response.wbsChargeCodeDetails,
              ];
            } else {
              this.wbsAPIData = response.wbsChargeCodeDetails;
            }

            this.searchList = [
              ...this.searchList,
              ...(response.wbsChargeCodeDetails?.map(
                (wbs) => wbs.wbsChargeCode
              ) ?? []),
            ];

            this.isFetchingWBS = false;
          }
        },
        error: (error) => {
          this.isFetchingWBS = false;
          this.handleError(error, 'Error fetching WBS charge codes');
        },
      });
  }

  onWBSScroll(event: any): void {
    const { scrollTop, scrollHeight, clientHeight } = event.target;

    if (
      this.searchList.length > 0 &&
      scrollTop + clientHeight + 1 >= scrollHeight
    ) {
      this.fetchWbsChargeCode(1);
    }
  }

  open(): void {
    const requestBody: ArchiveSaveModel = {
      opportunityNumber: this.dataOpportunityId,
      archiveID: this.archiveID,
      archiveNumber: this.archiveNumber,
      archiveName: this.archiveName,
      archiveFoldersArray: this.archiveFoldersArray,
      archiveFolder: this.archiveFolder,
      business: this.business,
      clientNumber: this.clientNumber,
      clientName: this.clientName,
      partner: this.partner,
      archiveStatus: this.archiveStatus,
      archiveDescription: this.archiveDescription,
      wbsChargeCode: String(this.wbsChargeCode),
      wbsDescription: this.wbsDescription,
    };

    this.intakeService
      .saveArchive(requestBody)
      .pipe(takeUntil(this.#unsubscriber$))
      .subscribe({
        next: (response) => {
          this.closeArchiveModel();
        },
        error: (error) => {
          this.handleError(error, 'Error saving archive');
          this.closeArchiveModel();
        },
      });
  }

  closeArchiveModel(): void {
    this.modalService.close();
  }

  changeValue(): void {
    this.isSaveButtonDisabled = true;
    this.selectedArchive = '';
    if (this.isElementInArray(this.wbsChargeCode, this.searchList)) {
      const selectedWBSCode: WbsChargeCodeDetailsModel | undefined =
        this.wbsAPIData.find((wbs) => wbs.wbsChargeCode === this.wbsChargeCode);
      if (selectedWBSCode) {
        this.wbsDescription = selectedWBSCode.wbsDescription;
        this.wbsChargeCode = selectedWBSCode.wbsChargeCode;
        if (this.wbsChargeCode) {
          this.archiveSelected();
        }
      }
    } else {
      if (
        this.wbsChargeCode &&
        this.wbsChargeCode.trim().length >=
          IntakeConstant.CHARACTER_LIMIT_IN_SEARCH &&
        this.wbsChargeCode.trim().length <= IntakeConstant.number_11
      ) {
        this.#wbsUnsubscriber$.next();
        this.fetchWbsChargeCode();
      }
    }
  }

  isElementInArray(element: string | null, array: string[]): boolean {
    return element ? array.includes(element) : false;
  }

  archiveSelected(): void {
    this.isSaveButtonDisabled = true;
    this.progressIndicatorService.show();
    this.intakeService
      .getWBSCodes(String(this.wbsChargeCode))
      .pipe(takeUntil(this.#unsubscriber$))
      .subscribe({
        next: (response: WbsDropdownModelResponseAll) => {
          if (response) {
            this.dropdowndata = response.archiveDetails;
            this.actionDropdownList = this.dropdowndata.map(
              (item: WbsDropdownResponseModel) => ({
                value: item.archiveNumber,
                heading: item.archiveName,
              })
            );
            this.prepopulateDropdown();
          }
          this.progressIndicatorService.hide();
        },
        error: (error) => {
          this.progressIndicatorService.hide();
          this.handleError(error, 'Error fetching WBS codes');
        },
      });
  }

  prepopulateDropdown(): void {
    if (this.initialSelectedArchive) {
      this.selectedArchive = this.initialSelectedArchive;
      this.archiveChanged();
    }
  }

  archiveChanged(): void {
    this.selectedArchiveDropdown = this.dropdowndata.find(
      (archive) => archive.archiveNumber === this.selectedArchive
    );

    if (this.selectedArchiveDropdown) {
      this.archiveNumber = this.selectedArchiveDropdown.archiveNumber;
      this.archiveID = this.selectedArchiveDropdown.archiveID;
      this.archiveName = this.selectedArchiveDropdown.archiveName;
      this.archiveFoldersArray =
        this.selectedArchiveDropdown.archiveFoldersArray;
      this.archiveFolder = this.selectedArchiveDropdown.archiveFolder;
      this.business = this.selectedArchiveDropdown.business;
      this.clientNumber = this.selectedArchiveDropdown.clientNumber;
      this.clientName = this.selectedArchiveDropdown.clientName;
      this.partner = this.selectedArchiveDropdown.partner;
      this.getArchiveStatus();
    }
  }

  getArchiveStatus(): void {
    this.progressIndicatorService.show();
    this.intakeService
      .getArchiveDetails(this.archiveNumber)
      .pipe(takeUntil(this.#unsubscriber$))
      .subscribe({
        next: (data: ArchiveDetailsModel) => {
          this.archiveStatus = data.archiveStatus;
          this.archiveDescription = data.archiveDescription;
          this.archiveUrl = data.archiveUrl;
          this.isSaveButtonDisabled = false;
          this.progressIndicatorService.hide();
        },
        error: (error) => {
          this.progressIndicatorService.hide();
          this.handleError(error, 'Error fetching archive details');
        },
      });
  }

  handleError(error: any, message: string): void {
    console.error(message, error);
  }

  createRequestBody(): wbsChargeCodeRequestModel {
    return {
      opportunityId: this.dataOpportunityId,
      submissionId: this.dataSubmissionId,
      searchTerm: this.wbsChargeCode?.trim() || IntakeConstant.EMPTY_SPACE,
      pageNumber: 0,
      pageSize: IntakeConstant.PAGE_NUMBER,
    };
  }

  ngOnDestroy(): void {
    this.#unsubscriber$.next();
    this.#unsubscriber$.complete();
    this.#wbsUnsubscriber$.next();
    this.#wbsUnsubscriber$.complete();
  }
}
